import React, { useState } from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import Activity from '@/components/shared/Activity';
import Button from '@/components/Button';
import RetriggerJobNotificationsModal from '@/components/modals/RetriggerJobNotificationsModal';
import { useJobStore } from '@core/job/useJobStore';
import { useAuditLogStore } from '@core/auditLog/useAuditLogStore';
import { Job, JobStatus } from '@core/generated/gql-types';

interface JobActivityProps {
  job: Job;
  eligibleCandidateCount: number;
  isLoadingEligibleCandidates?: boolean;
}

const JobActivity: React.FC<JobActivityProps> = ({
  job,
  eligibleCandidateCount,
  isLoadingEligibleCandidates = false,
}) => {
  const { retriggerJobNotifications } = useJobStore();
  const { fetchAuditLogsForObject } = useAuditLogStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFutureUnfilledJob =
    job.itemStatus === JobStatus.Unfilled &&
    dayjs(Number(job.startDate)).isAfter(dayjs());

  const handleRetriggerNotifications = async () => {
    setIsSubmitting(true);
    try {
      const success = await retriggerJobNotifications(job.id);
      if (!success) {
        throw new Error('Unable to retrigger job notifications.');
      }
      await fetchAuditLogsForObject(job.id);
      toast.success('Job notifications retriggered successfully.', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      setIsModalOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to retrigger job notifications.',
        {
          position: 'bottom-left',
          autoClose: 3000,
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const headerActions = isFutureUnfilledJob ? (
    <Button
      variant="outline"
      type="button"
      onClick={() => setIsModalOpen(true)}
      disabled={isLoadingEligibleCandidates || isSubmitting}
      className="shrink-0 py-2 text-sm whitespace-nowrap"
    >
      Retrigger Notifications
    </Button>
  ) : null;

  return (
    <>
      <Activity
        objectId={job.id}
        title="Recent Activity"
        headerActions={headerActions}
      />
      <RetriggerJobNotificationsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleRetriggerNotifications}
        eligibleCandidateCount={eligibleCandidateCount}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default JobActivity;
